// Types for daily out-of-hours schedule
export type DayOfWeek =
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday';

// Per-day schedule
export interface DaySchedule {
    enabled: boolean;
    allDay: boolean;
    startTime: number; // Minutes of day (0-1439)
    endTime: number; // Minutes of day (0-1439)
}

export type ScheduleMode = 'simple' | 'advanced';

// Full schedule data with dual storage for mode switching
export interface OutOfHoursScheduleData {
    mode: ScheduleMode;
    // Simple mode config - single time range for all days
    simple: {
        startTime: number;
        endTime: number;
    };
    // Advanced mode config - per-day settings
    advanced: Record<DayOfWeek, DaySchedule>;
}

// Legacy type for backwards compatibility
export type OutOfHoursSchedule = Record<DayOfWeek, DaySchedule>;

export const DAYS_OF_WEEK: { key: DayOfWeek; label: string }[] = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
];

const DEFAULT_START_TIME = 1020; // 17:00
const DEFAULT_END_TIME = 540; // 09:00

const DEFAULT_DAY_SCHEDULE: DaySchedule = {
    enabled: true,
    allDay: false,
    startTime: DEFAULT_START_TIME,
    endTime: DEFAULT_END_TIME
};

export function createDefaultScheduleData(): OutOfHoursScheduleData {
    return {
        mode: 'simple',
        simple: {
            startTime: DEFAULT_START_TIME,
            endTime: DEFAULT_END_TIME
        },
        advanced: {
            monday: { ...DEFAULT_DAY_SCHEDULE },
            tuesday: { ...DEFAULT_DAY_SCHEDULE },
            wednesday: { ...DEFAULT_DAY_SCHEDULE },
            thursday: { ...DEFAULT_DAY_SCHEDULE },
            friday: { ...DEFAULT_DAY_SCHEDULE },
            saturday: { ...DEFAULT_DAY_SCHEDULE, allDay: true },
            sunday: { ...DEFAULT_DAY_SCHEDULE, allDay: true }
        }
    };
}

// Convert "HH:MM" → minutes (e.g., "22:00" → 1320)
export function timeStringToMinutes(timeStr: string): number {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
}

// Convert minutes → "HH:MM" (e.g., 1320 → "22:00")
export function minutesToTimeString(
    minutes: number | null,
    defaultTime: string
): string {
    if (minutes === null) {
        return defaultTime;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// Convert minutes → "h:mm AM/PM" (e.g., 540 → "9:00 AM", 1020 → "5:00 PM")
export function minutesToTimeString12(
    minutes: number | null,
    defaultTime: string
): string {
    if (minutes === null) {
        return defaultTime;
    }
    const hours24 = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
    return `${hours12}:${mins.toString().padStart(2, '0')} ${period}`;
}
